import {
  CreateUserPersonalDataRequestWithAreaOfInterestDto,
  DictionaryTypeDto,
  UserAreaOfInterestItemDto,
  UserConsentDto,
  UserPersonalDataPropertiesDto,
} from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import { CheckboxField } from '@/components/form/CheckboxField.comp.tsx';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { ProgressIndicator } from '@/components/form/ProgressIndicator.comp.tsx';
import { TextAreaField } from '@/components/form/TextAreaField.comp.tsx';
import { useActiveAreaOfInterests } from '@/hooks/useActiveAreaOfInterests';
import { useCreateUserPersonalData } from '@/hooks/useCreateUserPersonalData';
import { useRevalidateOnLanguageChange } from '@/hooks/useRevalidateOnLanguageChange.ts';
import {
  DictionariesContextProps,
  useDictionariesContext,
} from '@/utils/providers/dictionariesContext/Dictionaries.context.ts';
import { DictionariesContextProvider } from '@/utils/providers/dictionariesContext/DictionariesContextProvider.tsx';
import { FormProvider } from '@/utils/providers/formProviderContext/FormProvider.tsx';
import { useUserContext } from '@/utils/providers/userContext/useUserContext';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';
import DataProcessingInfo from '@/views/process/components/personalDataForm/DataProcessingInfo.tsx';
import { AreasOfInterestSection } from '@/views/process/components/personalDataForm/sections/AreasOfInterestSection.tsx';
import DetailSection from '@/views/process/components/personalDataForm/sections/DetailsSection.tsx';
import { PersonalDataFormConsents } from '@/views/process/pages/personalDataForm/components/PersonalDataFormConsents.comp.tsx';
import {
  usePersonalDataFormValidation,
  UserPersonalDataProperties,
} from '@/views/process/pages/personalDataForm/usePersonalDataForm.validation.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';

interface FormData extends UserPersonalDataProperties {
  consents: UserConsentDto[];
  prioritizedAreas: UserAreaOfInterestItemDto[];
}

const findDictionaryItem = (
  dictionary: SelectSearchOptionModel[],
  value: string,
): SelectSearchOptionModel | undefined => {
  return dictionary?.find(item => item.value === value);
};

const getDataForPersonalDataWithInterests = (
  { consents, prioritizedAreas, ...formData }: FormData,
  userId: string = '',
  dictionaries: DictionariesContextProps,
): CreateUserPersonalDataRequestWithAreaOfInterestDto => {
  const personalData: UserPersonalDataPropertiesDto = {
    aboutYourself: formData.aboutYourself,
    areaOfInterestsUserInput: formData.areaOfInterestsUserInput,
    fieldOfStudy: formData.fieldOfStudy,
    firstName: formData.firstName,
    githubUrl: formData.githubUrl,
    lastName: formData.lastName,
    lessThan30Years: formData.isUnder30 ?? false,
    linkedInUrl: formData.linkedInUrl,
    phoneNumber: formData.phoneNumber,
    universityCustom: formData.universityCustom,
  };

  const dictionaryFieldMappings = [
    { field: 'studentStatus', type: DictionaryTypeDto.Status },
    { field: 'university', type: DictionaryTypeDto.UniversityName },
    { field: 'location', type: DictionaryTypeDto.Location },
    { field: 'typeOfStudy', type: DictionaryTypeDto.TypeOfStudy },
    { field: 'yearOfStudy', type: DictionaryTypeDto.YearOfStudy },
  ] as const;

  dictionaryFieldMappings.forEach(({ field, type }) => {
    const fieldValue = formData[field]?.value;
    if (fieldValue) {
      const item = findDictionaryItem(dictionaries[type], fieldValue);
      if (item) {
        personalData[field] = {
          code: item.label,
          id: parseInt(item.value),
          type,
        };
      }
    }
  });

  return {
    areaOfInterests: {
      areaOfInterestItems: prioritizedAreas,
      userId,
    },
    consents,
    personalData,
  };
};

const defaultValues: UserPersonalDataProperties = {
  aboutYourself: undefined,
  areaOfInterestsUserInput: undefined,
  fieldOfStudy: '',
  firstName: '',
  githubUrl: undefined,
  isUnder30: false,
  lastName: '',
  linkedInUrl: undefined,
  location: undefined,
  phoneNumber: '',
  studentStatus: undefined,
  typeOfStudy: undefined,
  university: undefined,
  universityCustom: undefined,
  yearOfStudy: undefined,
};

const PersonalDataFormInner: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.process.personalData');
  const { user } = useUserContext();
  const [prioritizedAreas, setPrioritizedAreas] = useState<UserAreaOfInterestItemDto[]>([]);
  const [consents, setConsents] = useState<UserConsentDto[]>([]);
  const [step, setStep] = useState(1);
  const [showConsentWarning, setShowConsentWarning] = useState(false);
  const { createUserPersonalDataWithInterests, isLoading } = useCreateUserPersonalData();
  const { activeAreaOfInterests } = useActiveAreaOfInterests();
  const validation = usePersonalDataFormValidation();
  const dictionaries = useDictionariesContext();

  const formMethods = useForm<UserPersonalDataProperties>({
    defaultValues,
    resolver: yupResolver(validation),
  });
  useRevalidateOnLanguageChange(formMethods);

  const {
    clearErrors,
    formState: { errors },
    register,
    setValue,
  } = formMethods;

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof UserPersonalDataProperties;
    setValue(fieldName, value);
    if (value && errors[fieldName]) {
      clearErrors(fieldName);
    }
  };

  const handleAreasChange = (areas: UserAreaOfInterestItemDto[]) => {
    setPrioritizedAreas(areas);
  };

  const handleConsentsChange = (consentId: UserConsentDto) => () => {
    setConsents(prev => {
      const newConsents = prev.includes(consentId)
        ? prev.filter(a => a !== consentId)
        : [...prev, consentId];
      if (
        consentId === UserConsentDto.AdditionalPersonalDataProcessingConsentOrange &&
        newConsents.includes(UserConsentDto.AdditionalPersonalDataProcessingConsentOrange)
      ) {
        setShowConsentWarning(false);
      }
      return newConsents;
    });
  };

  const handleSubmit: SubmitHandler<UserPersonalDataProperties> = async form => {
    if (!user) {
      return;
    }
    if (!consents.includes(UserConsentDto.AdditionalPersonalDataProcessingConsentOrange)) {
      setShowConsentWarning(true);
      return;
    }
    createUserPersonalDataWithInterests({
      data: getDataForPersonalDataWithInterests(
        { ...form, consents, prioritizedAreas },
        user.userId,
        dictionaries,
      ),
      userId: user.userId,
    });
  };

  return (
    <div className="py-4 w-full align-self-center">
      <h2 className="mb-4 font-bold text-2xl">{t('heading')}</h2>
      <ProgressIndicator currentProgress={step} maxProgress={3} />
      <FormProvider validation={validation} {...formMethods}>
        <Form className="m-auto" onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <div className="flex flex-col gap-8">
            <DetailSection formMethods={formMethods} />

            {step >= 2 && (
              <AreasOfInterestSection
                activeAreaOfInterests={activeAreaOfInterests ?? []}
                formMethods={formMethods}
                handleAreasChange={handleAreasChange}
                handleTextAreaChange={handleTextAreaChange}
                prioritizedAreas={prioritizedAreas}
                t={t}
              />
            )}

            {step >= 3 && (
              <div className="flex flex-col gap-4">
                <TextAreaField
                  className="lg:col-span-3"
                  errors={errors}
                  label={t('fields.aboutYourself.label')}
                  maxLength={1000}
                  name="aboutYourself"
                  onChange={handleTextAreaChange}
                  placeholder={t('fields.aboutYourself.placeholder')}
                  register={register}
                  rows={7}
                />
                <div className="flex flex-col gap-2">
                  <CheckboxField
                    content={t('fields.isUnder30')}
                    errors={errors}
                    name="isUnder30"
                    register={register}
                    required
                    t={t}
                  />
                  <PersonalDataFormConsents
                    consents={consents}
                    onConsentsChange={handleConsentsChange}
                    showConsentWarning={showConsentWarning}
                  />
                  <DataProcessingInfo />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-s">
              <div className="flex gap-3 mb-2">
                {step < 3 && (
                  <Button
                    className="btn btn-primary align-self-stretch align-self-md-start"
                    onClick={() => setStep(prevState => prevState + 1)}
                  >
                    {t('button.next')}
                  </Button>
                )}
                {step === 3 && (
                  <ButtonWithLoading
                    className="mb-4"
                    disabled={prioritizedAreas.length < 1}
                    isLoading={isLoading}
                    type="submit"
                  >
                    {t('button.next')}
                  </ButtonWithLoading>
                )}
              </div>
            </div>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};

const PersonalDataForm: React.FC = () => {
  return (
    <DictionariesContextProvider>
      <PersonalDataFormInner />
    </DictionariesContextProvider>
  );
};

export default PersonalDataForm;
